import { Dropdown } from "semantic-ui-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toPng } from "html-to-image";
import { drawChartRow, drawChartSection } from "../pdfCharts";
import { renderEnergyChartsForPdf, renderComparisonForPdf, renderMonthlyForPdf } from "./pdfEnergyCharts";
import { formatWhole, formatMagnitude } from "../../../../utils/numberFormat";

// ── Helpers que replican la lógica de formato de la vista ──────────────────

const isIntegerRow = (item) =>
  item.includes("cost") || item === "total_energy_consumption_kpis";

const getNeededDecimals = (value) => {
  if (typeof value !== "number" || isNaN(value) || value === 0) return 2;
  const abs = Math.abs(value);
  if (abs < 0.0000005) return -1;
  if (abs >= 1) return 2;
  const leadingZeros = Math.floor(-Math.log10(abs));
  return Math.max(2, leadingZeros + 2);
};

const getRowDecimalPlaces = (item, values) => {
  const nonZero = values.filter((v) => typeof v === "number" && !isNaN(v) && v !== 0);
  if (isIntegerRow(item)) {
    if (nonZero.length === 0 || nonZero.every((v) => Math.abs(v) >= 1)) return 0;
  }
  let maxDecimals = 2;
  for (const v of values) {
    if (typeof v !== "number" || isNaN(v) || v === 0) continue;
    const needed = getNeededDecimals(v);
    if (needed === -1) continue;
    if (needed > maxDecimals) maxDecimals = needed;
  }
  return Math.min(maxDecimals, 6);
};

// AJUSTES DE MAGNITUDES NUMÉRICAS: antes esta función usaba
// `toLocaleString("es-AR")` (separador de miles ".", decimal ",") para las
// filas "enteras" y no agrupaba miles en absoluto para el resto — ninguna
// de las dos coincidía con el estándar de la app (espacio fino + punto
// decimal, vía numberFormat.js). `rowDecimals` (Regla 2+: 2 a 6 decimales
// adaptativos por fila, ver getRowDecimalPlaces) se mantiene igual — es una
// extensión legítima ya usada en GEIPDFReport.js, no algo que la regla de
// miles/decimales prohíba.
const formatWithDecimals = (value, decimals) => {
  if (typeof value !== "number" || isNaN(value)) return "-";
  if (value === 0) return "-";
  if (Math.abs(value) < 0.0000005) return decimals === 0 ? "0" : (0).toFixed(decimals);
  const formatted = decimals === 0 ? formatWhole(value) : formatMagnitude(value, decimals);
  // autoTable dibuja esta celda con doc.text() nativo (no SVG) — el espacio
  // fino (U+202F) no existe en WinAnsi y rompe el texto (mismo problema ya
  // resuelto en GEI/Social/Residuos/Efluentes/Agua/Combustibles).
  return formatted.replace(/ /g, " ");
};

const PER_EMPLOYEE_ITEMS = new Set([
  "electricity_standard_domestic_kpis",
  "renewable_energies_ppa_domestic_kpis",
  "renewable_energies_on_site_domestic_kpis",
  "natural_gas_domestic_kpis",
  "glp_domestic_kpis",
]);

const PER_UNIT_ITEMS = new Set([
  "electricity_standard_combined_kpis",
  "electricity_standard_production_kpis",
  "renewable_energies_ppa_combined_kpis",
  "renewable_energies_ppa_production_kpis",
  "renewable_energies_on_site_combined_kpis",
  "renewable_energies_on_site_production_kpis",
  "natural_gas_combined_kpis",
  "natural_gas_cogeneration_kpis",
  "natural_gas_production_operations_kpis",
  "glp_combined_kpis",
  "glp_cogeneration_kpis",
  "glp_production_operations_kpis",
  "heavy_fuel_oil_total_kpis",
  "light_fuel_oil_total_kpis",
  "coal_total_kpis",
  "diesel_combined_kpis",
  "diesel_cogeneration_kpis",
  "diesel_production_operations_kpis",
  "diesel_forklifts_kpis",
  "biodiesel_combined_kpis",
  "biodiesel_cogeneration_kpis",
  "biodiesel_production_operations_kpis",
  "biodiesel_forklifts_kpis",
  "gasoline_combined_kpis",
  "gasoline_cogeneration_kpis",
  "gasoline_production_operations_kpis",
  "gasoline_forklifts_kpis",
  "biofuel_combined_kpis",
  "biofuel_cogeneration_kpis",
  "biofuel_production_operations_kpis",
  "biofuel_forklifts_kpis",
  "biomass_total_kpis",
  "steam_total_kpis",
  "total_energy_kpis",
]);

const buildUnitLabel = (item, unit, t) => {
  if (!unit) return "-";
  if (item.includes("per_employee") || PER_EMPLOYEE_ITEMS.has(item))
    return `${unit} / ${t("employee")}`;
  if (item.includes("per_unit") || PER_UNIT_ITEMS.has(item))
    return `${unit} / ${t("unit")}`;
  return unit;
};

// ── Colores de pill (igual que la vista) ──────────────────────────────────

const getPillColor = (val, prevVal) => {
  if (val == null || val === 0) return null;
  if (prevVal == null || prevVal === 0) return [208, 208, 208]; // neutral
  if (val > prevVal) return [244, 168, 164]; // sube → rojo
  if (val < prevVal) return [168, 213, 181]; // baja → verde
  return [208, 208, 208];
};

// ── Componente ────────────────────────────────────────────────────────────

const ExportPDFMenuButton = ({
  conceptos,
  years,
  totales,
  selectedUnits,
  energyKPIsCodes,
  t,
  language,
  getIndicators,
  summaryRef,
  tableGrafRef,
  comparisonChartRef,
  monthlyChartRef,
  resultsByYear,
  resultsProductions,
  baseline,
  configFormData,
  filters,
  siteName = "",
}) => {
  const generatePDF = async (includeCharts = false) => {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yOffset = 36;

    doc.setFontSize(14);
    doc.setFont(undefined, "bold");
    doc.setTextColor(20, 40, 80);
    doc.setCharSpace(1.5);
    doc.text("Reporte Energético", margin, yOffset);
    doc.setCharSpace(0);
    yOffset += 16;

    if (siteName) {
      doc.setFontSize(10);
      doc.setFont(undefined, "normal");
      doc.setTextColor(60, 80, 120);
      doc.text(siteName.toLocaleUpperCase(), margin, yOffset);
      yOffset += 14;
    }

    doc.setFontSize(8);
    doc.setFont(undefined, "normal");
    doc.setTextColor(130, 130, 130);
    doc.setCharSpace(0.8);
    doc.text(
      `Generado el ${new Date().toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" })}`,
      margin,
      yOffset
    );
    doc.setCharSpace(0);
    yOffset += 18;

    doc.setDrawColor(210, 210, 210);
    doc.line(margin, yOffset, pageWidth - margin, yOffset);
    yOffset += 12;

    // ── Gráficos opcionales ──────────────────────────────────────────────
    const captureElement = async (ref) => {
      if (!ref?.current) return null;
      try {
        return await toPng(ref.current, {
          pixelRatio: 2,
          filter: (node) => node.dataset?.pdfExclude !== "true",
        });
      } catch {
        return null;
      }
    };

    const imgWidth = pageWidth - margin * 2;

    const addImageToPDF = (img) => {
      if (!img) return;
      const imgProps = doc.getImageProperties(img);
      const naturalH = (imgProps.height * imgWidth) / imgProps.width;
      const maxH = pageHeight - margin * 2;

      // Nunca se achica el ancho -- si el gráfico solo (a ancho completo)
      // ya es más alto que una página entera, se reduce proporcionalmente
      // (único caso donde no hay forma de evitarlo sin cortar contenido).
      let drawWidth = imgWidth;
      let drawHeight = naturalH;
      if (naturalH > maxH) {
        drawHeight = maxH;
        drawWidth = imgWidth * (maxH / naturalH);
      }

      // Si no entra en lo que queda de la página actual, pasar a una
      // nueva en vez de achicarlo más para que "entre" -- eso era lo que
      // dejaba espacio en blanco a la derecha en el segundo gráfico.
      const remaining = pageHeight - margin - yOffset;
      if (drawHeight > remaining) {
        doc.addPage();
        yOffset = margin;
      }

      doc.addImage(img, "PNG", margin, yOffset, drawWidth, drawHeight);
      yOffset += drawHeight + 10;
    };

    if (includeCharts) {
      const summaryImg = await captureElement(summaryRef);
      if (summaryImg) addImageToPDF(summaryImg);

      // Gráficos de TableGraf.js (dona + 2 barras) como SVG vectorial en vez
      // de rasterizados con toPng — mismo enfoque que en GeneralDashboard.js.
      const { donut, barConsumo, barCosto } = await renderEnergyChartsForPdf(totales, filters, t);
      const energyCards = [
        { title: t("energy_sources"), ...donut },
        { title: t("electric_energy_consumption"), ...barConsumo },
        { title: t("electric_energy_costs"), ...barCosto },
      ];
      const CHART_ROW_H = 210;
      const remaining = pageHeight - margin - yOffset;
      if (CHART_ROW_H > remaining) {
        doc.addPage();
        yOffset = margin;
      }
      await drawChartRow(doc, energyCards, { x: margin, y: yOffset, availW: imgWidth, cardH: CHART_ROW_H, emptyMessage: t("no_data_available") });
      yOffset += CHART_ROW_H + 10;
      donut.cleanup?.();
      barConsumo.cleanup?.();
      barCosto.cleanup?.();

      // "Evolución de Consumos" e "Intensidad" (DualAxes: barra+línea /
      // línea+línea) como SVG vectorial, igual que los de tableGrafRef, pero
      // como sección de ancho completo (sin tarjeta gris, leyenda arriba)
      // en vez de tarjeta — mismo estilo que tienen en pantalla (CollapsibleSection).
      // Los dos entran en la misma hoja: se reparte entre ambos el espacio
      // que quede en la página actual, o el de una hoja nueva completa si lo
      // que resta ahora es muy poco para los dos.
      const GAP = 10;
      const MIN_BOTH_H = 480; // 240pt mínimo cada uno
      let availForBoth = pageHeight - margin - yOffset - GAP;
      if (availForBoth < MIN_BOTH_H) {
        doc.addPage();
        yOffset = margin;
        availForBoth = pageHeight - margin - yOffset - GAP;
      }
      const DUAL_AXES_ROW_H = Math.floor(availForBoth / 2);

      const comparison = await renderComparisonForPdf(resultsByYear, t);
      await drawChartSection(doc, { x: margin, y: yOffset, width: imgWidth, height: DUAL_AXES_ROW_H, title: t("electric_energy_consumption_evolution"), emptyMessage: t("no_data_available"), ...comparison });
      yOffset += DUAL_AXES_ROW_H + GAP;
      comparison.cleanup?.();

      const monthly = await renderMonthlyForPdf(resultsByYear, resultsProductions, t);
      await drawChartSection(doc, { x: margin, y: yOffset, width: imgWidth, height: DUAL_AXES_ROW_H, title: t("electric_energy_intensity"), emptyMessage: t("no_data_available"), ...monthly });
      yOffset += DUAL_AXES_ROW_H + GAP;
      monthly.cleanup?.();
    }

    if (yOffset + 60 > pageHeight - margin) {
      doc.addPage();
      yOffset = margin;
    }

    // ── Construcción de filas con la misma lógica que la vista ───────────

    const yearKeys = years.map(String);

    // rawValues[rowIdx][yr] = valor original sin conversión (para comparar pills)
    const rawValues = conceptos.map((item) => {
      const vals = {};
      years.forEach((yr) => {
        vals[String(yr)] = totales[item]?.[yr]?.total ?? 0;
      });
      return vals;
    });

    const tableRows = conceptos.map((item) => {
      // Recopilar valores para determinar decimales
      const rowValuesRaw = years
        .map((y) => totales[item]?.[y]?.total)
        .filter((v) => typeof v === "number" && !isNaN(v));
      const lbTotal = totales[item]?.[getIndicators(item)]?.total;
      if (typeof lbTotal === "number" && !isNaN(lbTotal)) rowValuesRaw.push(lbTotal);

      // Downgrade MWh → kWh si los valores son muy pequeños
      const baseUnit = (selectedUnits[item] || "").trim().toLowerCase();
      let unitFactor = 1;
      let unitStr = selectedUnits[item] || "";
      if (baseUnit === "mwh") {
        const nonZero = rowValuesRaw.filter((v) => v !== 0);
        const maxAbs = nonZero.length > 0 ? Math.max(...nonZero.map(Math.abs)) : 0;
        if (maxAbs > 0 && maxAbs < 1) {
          unitFactor = 1000;
          unitStr = "kWh";
        }
      }

      const displayValues = rowValuesRaw.map((v) => v * unitFactor);
      const rowDecimals = getRowDecimalPlaces(item, displayValues);

      const row = {
        code: energyKPIsCodes[item] || "-",
        indicator: t(item),
      };

      years.forEach((yr) => {
        const val = (totales[item]?.[yr]?.total || 0) * unitFactor;
        row[String(yr)] = formatWithDecimals(val, rowDecimals);
      });

      const lbVal = (totales[item]?.[getIndicators(item)]?.total || 0) * unitFactor;
      row.lb = formatWithDecimals(lbVal, rowDecimals);
      row.units = buildUnitLabel(item, unitStr, t);

      return row;
    });

    const tableColumns = [
      { header: t("code"),       dataKey: "code" },
      { header: t("indicators"), dataKey: "indicator" },
      ...years.map((y) => ({ header: String(y), dataKey: String(y) })),
      { header: t("LB"),         dataKey: "lb" },
      { header: t("units"),      dataKey: "units" },
    ];

    const ROW_GAP = 4;
    const CORNER_R = 4;
    const pendingRows = {};

    // ── Column width calculation ─────────────────────────────────────────────
    doc.setFontSize(7);
    doc.setFont(undefined, "normal");
    const YEAR_COL_W = 48;
    const unitsHeader = t("units") || "Unidades";
    const unitsHeaderW = doc.getTextWidth(unitsHeader) + unitsHeader.length * 0.4 + 14;
    const unitsDataW = Math.max(...tableRows.map((r) => doc.getTextWidth(r.units || ""))) + 10;
    const unitsColW = Math.max(unitsHeaderW, unitsDataW, 50);
    const totalFixedW = 38 + 30 + unitsColW + years.length * YEAR_COL_W;
    const indicatorColW = pageWidth - 2 * margin - totalFixedW;
    const yearColStyles = {};
    years.forEach((yr) => { yearColStyles[String(yr)] = { halign: "center", cellWidth: YEAR_COL_W }; });

    autoTable(doc, {
      startY: yOffset,
      columns: tableColumns,
      body: tableRows,
      styles: {
        fontSize: 7,
        cellPadding: { top: 5, right: 4, bottom: 5, left: 4 },
        halign: "center",
        valign: "middle",
        fillColor: [237, 235, 235],
        textColor: [255, 255, 255], // invisible — redrawn in didDrawCell
        lineWidth: 0,
      },
      headStyles: {
        fillColor: [237, 237, 237],
        textColor: [237, 237, 237], // invisible — redrawn in didDrawCell
        fontStyle: "bold",
        fontSize: 8,
        lineWidth: 0,
        cellPadding: { top: 7, right: 4, bottom: 7, left: 4 },
      },
      columnStyles: {
        code:      { halign: "left", cellWidth: 38 },
        indicator: { halign: "left", cellWidth: indicatorColW },
        lb:        { halign: "center", cellWidth: 30 },
        units:     { halign: "left", cellWidth: unitsColW },
        ...yearColStyles,
      },
      theme: "plain",
      margin: { left: margin, right: margin },
      didDrawCell: (data) => {
        if (data.section === "head") {
          const { x, y, width, height } = data.cell;
          const colIdx = data.column.index;
          if (colIdx > 0) {
            doc.setDrawColor(210, 208, 208);
            doc.setLineWidth(0.3);
            doc.line(x, y + 3, x, y + height - 3);
          }
          const text = String(data.cell.raw || "");
          if (!text) return;
          doc.setFontSize(6);
          doc.setFont(undefined, "bold");
          doc.setTextColor(40, 40, 40);
          doc.setCharSpace(0.8);
          doc.text(text, x + width / 2, y + height / 2 + 3, { align: "center" });
          doc.setCharSpace(0);
          return;
        }
        if (data.section !== "body") return;

        const rowIdx = data.row.index;
        const colIdx = data.column.index;
        const numCols = data.table.columns.length;

        if (!pendingRows[rowIdx]) pendingRows[rowIdx] = [];
        pendingRows[rowIdx][colIdx] = {
          x: data.cell.x,
          y: data.cell.y,
          width: data.cell.width,
          height: data.cell.height,
          text: String(data.cell.raw || ""),
          colKey: String(data.column.dataKey),
        };

        if (colIdx !== numCols - 1) return;

        // ── Last column reached: draw rounded row background then all content ──
        const cells = pendingRows[rowIdx];
        const first = cells[0];
        const rowX = margin;
        const rowW = pageWidth - 2 * margin;
        const rowY = first.y + ROW_GAP / 2;
        const rowH = first.height - ROW_GAP;

        doc.setFillColor(237, 235, 235);
        doc.roundedRect(rowX, rowY, rowW, rowH, CORNER_R, CORNER_R, "F");

        // Column separators
        doc.setDrawColor(210, 208, 208);
        doc.setLineWidth(0.3);
        let sepX = rowX;
        for (let i = 0; i < cells.length - 1; i++) {
          if (cells[i]) sepX += cells[i].width;
          doc.line(sepX, rowY + 3, sepX, rowY + rowH - 3);
        }

        for (let i = 0; i < cells.length; i++) {
          const cell = cells[i];
          if (!cell) continue;
          const { x, y, width, height, text, colKey } = cell;
          const centerY = rowY + rowH / 2 + 2.5;

          if (yearKeys.includes(colKey)) {
            const rowVals = rawValues[rowIdx];
            if (rowVals) {
              const val = rowVals[colKey];
              const yrIdx = years.findIndex((yy) => String(yy) === colKey);
              const prevYr = yrIdx < years.length - 1 ? years[yrIdx + 1] : null;
              const prevVal = prevYr != null ? rowVals[String(prevYr)] : null;
              const color = getPillColor(val, prevVal);
              if (color && text && text !== "-") {
                const pillW = Math.min(width - 8, 58);
                const pillH = Math.max(rowH - 6, 10);
                const pillX = x + (width - pillW) / 2;
                const pillY = rowY + (rowH - pillH) / 2;
                doc.setFillColor(...color);
                doc.roundedRect(pillX, pillY, pillW, pillH, 3, 3, "F");
                doc.setFontSize(7);
                doc.setFont(undefined, "bold");
                doc.setTextColor(40, 40, 40);
                doc.text(text, pillX + pillW / 2, pillY + pillH / 2 + 2.5, { align: "center" });
                continue;
              }
            }
            if (text && text !== "-") {
              doc.setFontSize(7);
              doc.setFont(undefined, "normal");
              doc.setTextColor(40, 40, 40);
              doc.text(text, x + width / 2, centerY, { align: "center" });
            }
            continue;
          }

          if (!text || text === "-") continue;
          doc.setFontSize(7);
          doc.setFont(undefined, "normal");
          doc.setTextColor(40, 40, 40);

          if (colKey === "indicator") {
            const maxW = width - 8;
            const lines = doc.splitTextToSize(text, maxW);
            const lineH = 9;
            const totalH = lines.length * lineH;
            const textStartY = y + (height - totalH) / 2 + lineH * 0.75;
            doc.text(lines, x + 4, textStartY, { align: "left" });
          } else if (colKey === "code" || colKey === "units") {
            doc.text(text, x + 4, centerY, { align: "left" });
          } else {
            doc.text(text, x + width / 2, centerY, { align: "center" });
          }
        }

        delete pendingRows[rowIdx];
      },
    });

    const safeSite = siteName ? `_${siteName.replace(/\s+/g, "_")}` : "";
    doc.save(
      includeCharts
        ? `reporte_energia${safeSite}_con_graficos_${new Date().getFullYear()}.pdf`
        : `reporte_energia${safeSite}_${new Date().getFullYear()}.pdf`
    );
  };

  const options = [
    { key: "sin", text: "Solo tabla",   icon: "table",     onClick: () => generatePDF(false) },
    { key: "con", text: "Con gráficos", icon: "chart bar", onClick: () => generatePDF(true) },
  ];

  return (
    <div data-pdf-exclude="true" style={{ display: "contents" }}>
      <Dropdown
        pointing="top right"
        icon={null}
        trigger={
          <button className="kpi-export-icon-btn" title="Descargar PDF">
            <i className="download icon" style={{ color: "#555" }} />
          </button>
        }
        options={options}
      />
    </div>
  );
};

export default ExportPDFMenuButton;
